import { readString } from 'react-papaparse';

/* CSV parsing for the map's data files.
 *
 * Kept apart from `SessionContext` because it is pure: no React, no d3, no map. That
 * makes the one thing here that fails SILENTLY -- the gauge block's two shapes --
 * testable, which `src/parseGauges.test.js` does.
 */

/* The values file is one row per AREA, one integer per DATE:
 *
 *     area_key, instrument_count, 2024-06-12, 2024-06-13, ...
 *
 * That is true of the H3 export and of the administrative export alike -- `export_map
 * --source admin` changes the key column from `h3_hexagon` to `admin_id` and nothing
 * else. So this parser is shared, and the H3-to-ADM switch does not touch the timeline
 * model at all.
 */
export const parseAreasCSV = async (csv) => new Promise((resolve) => {
  readString(csv, {
    complete: ({ data }) => {
      // get dates from header row
      const dates = data[0].slice(2);

      // remove header row
      const csvRows = data.slice(1, -1);

      // get area keys and gauge counts from first two columns
      const areaKeys = csvRows.map((row) => row[0]);
      const gaugeCounts = csvRows.map((row) => parseInt(row[1], 10));

      const wliTimeline = csvRows.map((row) => (
        row
          .slice(2) // remove key and count columns
          .map((str) => (str ? parseInt(str, 10) : null)) // convert to int
      ));

      resolve([areaKeys, dates, wliTimeline, gaugeCounts]);
    },
  });
});

/* The gauge block comes in TWO shapes and the difference is silent, so it is sniffed
 * rather than assumed.
 *
 *   * the published `wli.json` today  -- headerless, positional `key, lat, lng`;
 *   * `export_map --source admin`     -- a HEADER row, and the columns
 *     `instrument_id, name, provider_id, provider_name, site_type, lat, lon, unit, ...`
 *     with NO area key at all (gauge identity survives export, spec 5.2).
 *
 * Parsed positionally, the second shape reads `instrument_id` as the area key and the
 * gauge's NAME as its latitude -- every coordinate NaN, no nearby gauges, no arcs, and
 * no error anywhere. That is why this sniffs for a header instead of trusting position:
 * swapping the fixture for the real table has to stay a change of URL.
 */
const GAUGE_KEY_COLUMNS = ['admin_id', 'h3_hexagon', 'area_id', 'areakey'];

export const parseGaugesCSV = async (gauges) => new Promise((resolve) => {
  readString(gauges, {
    complete: ({ data }) => {
      if (!data.length) { resolve([]); return; }

      const header = data[0].map((c) => String(c).trim().toLowerCase());
      const latIndex = header.indexOf('lat');
      const lngIndex = header.findIndex((c) => c === 'lon' || c === 'lng');
      const hasHeader = latIndex !== -1 && lngIndex !== -1;

      if (!hasHeader) {
        // The trailing newline yields one empty row whose coordinates are NaN. It has
        // always been in `gaugePositions`; it survives only because a NaN distance
        // compares false against the radius. Dropping it here makes the list mean what
        // it says.
        resolve(data
          .map((gauge) => {
            const [areaKey, lat, lng] = gauge;
            return {
              areaKey, hexagonCode: areaKey, lat: Number(lat), lng: Number(lng)
            };
          })
          .filter(({ lat, lng }) => Number.isFinite(lat) && Number.isFinite(lng)));
        return;
      }

      // An area key is optional. Without one the nearby-gauge arcs draw at ground level
      // instead of at the area's extruded height -- a cosmetic loss, not a broken map.
      const keyIndex = header.findIndex((c) => GAUGE_KEY_COLUMNS.includes(c));
      resolve(data.slice(1)
        .filter((row) => row.length > Math.max(latIndex, lngIndex))
        .map((row) => {
          const areaKey = keyIndex === -1 ? undefined : row[keyIndex];
          return {
            areaKey,
            hexagonCode: areaKey,
            lat: Number(row[latIndex]),
            lng: Number(row[lngIndex])
          };
        })
        .filter(({ lat, lng }) => Number.isFinite(lat) && Number.isFinite(lng)));
    },
  });
});
